<template>
  <div class="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-2 px-3 sm:py-6 sm:px-4">
    <div class="fixed top-4 right-4 z-30">
      <button 
        @click="toggleTheme" 
        class="p-3 rounded-full bg-white/95 dark:bg-gray-800/95 backdrop-blur-lg shadow-xl hover:shadow-2xl transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 hover:scale-110 active:scale-95 border border-gray-200/50 dark:border-gray-700/50"
        :title="isDark ? 'Mudar para modo claro' : 'Mudar para modo escuro'"
      >
        <svg v-if="!isDark" class="w-5 h-5 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clip-rule="evenodd"></path>
        </svg>
        <svg v-else class="w-5 h-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
          <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z"></path>
        </svg>
      </button>
    </div>

    <Transition
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="opacity-0 transform -translate-y-2 scale-95"
      enter-to-class="opacity-100 transform translate-y-0 scale-100"
      leave-active-class="transition-all duration-200 ease-in"
      leave-from-class="opacity-100 transform translate-y-0 scale-100"
      leave-to-class="opacity-0 transform -translate-y-2 scale-95"
    >
      <div v-if="showSuccess || showError" class="fixed top-4 left-4 right-4 z-50 flex justify-center">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 p-4 sm:p-6 max-w-md w-full mx-4 backdrop-blur-lg">
          <div v-if="showSuccess" class="text-center">
            <div class="mx-auto flex items-center justify-center h-14 w-14 rounded-full bg-gradient-to-r from-green-400 to-emerald-500 mb-4 animate-bounce">
              <svg class="h-7 w-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
            <h3 class="text-lg font-bold text-gray-900 dark:text-white mb-2">
              ✅ Evento Registrado!
            </h3>
            <p class="text-sm text-gray-600 dark:text-gray-300 mb-1">
              Seu evento foi registrado com sucesso
            </p>
            <p v-if="protocolo" class="text-xs font-mono bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded-lg mb-4">
              Protocolo: <span class="font-bold text-green-600 dark:text-green-400">{{ protocolo }}</span>
            </p>
            <button 
              @click="closeSuccess" 
              class="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-semibold py-2.5 px-4 rounded-xl transition-all duration-200 transform hover:scale-105 active:scale-95"
            >
              Perfeito! 🎉
            </button>
          </div>

          <div v-if="showError" class="text-center">
            <div class="mx-auto flex items-center justify-center h-14 w-14 rounded-full bg-gradient-to-r from-red-400 to-rose-500 mb-4 animate-pulse">
              <svg class="h-7 w-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
              </svg>
            </div>
            <h3 class="text-lg font-bold text-gray-900 dark:text-white mb-2">
              ⚠️ Ops! Algo deu errado
            </h3>
            <p class="text-sm text-gray-600 dark:text-gray-300 mb-4 bg-red-50 dark:bg-red-900/20 p-3 rounded-lg border border-red-200 dark:border-red-800">
              {{ errorMessage }}
            </p>
            <button 
              @click="closeError" 
              class="w-full bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white font-semibold py-2.5 px-4 rounded-xl transition-all duration-200 transform hover:scale-105 active:scale-95"
            >
              Tentar Novamente
            </button>
          </div>
        </div>
      </div>
    </Transition>

    <div class="max-w-4xl mx-auto">
      <div class="text-center pt-1">
        <img src="/ccdlogo.png" alt="CCD Logo" class="w-64 sm:w-72 object-contain mx-auto mb-4">
        <h1 class="text-2xl sm:text-4xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent leading-tight">
          Registro de Evento Adverso
        </h1>
        <p class="mt-2 text-sm sm:text-lg text-gray-600 dark:text-gray-400 px-4 max-w-2xl mx-auto">
          Preencha todos os campos obrigatórios para registrar o evento de forma segura e eficiente
        </p>
      </div>

      <form @submit.prevent="submitForm" class="mt-8 bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 p-4 sm:p-8 space-y-6 sm:space-y-8">
        
        <div class="border-b border-gray-200/70 dark:border-gray-700/70 pb-6">
          <h2 class="text-lg sm:text-xl font-bold text-gray-800 dark:text-white mb-4 flex items-center">
            <span class="w-8 h-8 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3 shadow-lg">1</span>
            Informações Básicas
          </h2>
          
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            <div>
              <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Responsável pela Comunicação
              </label>
              <input 
                v-model="form.responsavel" 
                type="text" 
                class="w-full px-4 py-3.5 border border-gray-300/70 dark:border-gray-600/70 bg-white/90 dark:bg-gray-700/90 text-gray-900 dark:text-white rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 backdrop-blur-sm"
                placeholder="Nome do responsável (opcional)"
              >
            </div>
            
            <div>
              <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Data do Evento <span class="text-red-500">*</span>
              </label>
              <input 
                v-model="form.dataEvento" 
                type="date" 
                required
                class="w-full px-4 py-3.5 border border-gray-300/70 dark:border-gray-600/70 bg-white/90 dark:bg-gray-700/90 text-gray-900 dark:text-white rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 backdrop-blur-sm max-w-full"
              >
            </div>
          </div>
        </div>

        <div class="border-b border-gray-200/70 dark:border-gray-700/70 pb-6">
          <h2 class="text-lg sm:text-xl font-bold text-gray-800 dark:text-white mb-4 flex items-center">
            <span class="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3 shadow-lg">2</span>
            Dados do Usuário
          </h2>
          
          <div class="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
            <div class="lg:col-span-2">
              <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Nome Completo do Usuário <span class="text-red-500">*</span>
              </label>
              <input 
                v-model="form.nomeUsuario" 
                type="text" 
                required
                class="w-full px-4 py-3.5 text-sm sm:text-base border border-gray-300/70 dark:border-gray-600/70 bg-white/90 dark:bg-gray-700/90 text-gray-900 dark:text-white rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 backdrop-blur-sm"
                placeholder="Nome completo do paciente"
              >
            </div>
            
            <div>
              <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                CPF do Usuário
              </label>
              <input 
                v-model="form.cpfUsuario" 
                type="text" 
                @input="formatCPF"
                maxlength="14"
                class="w-full px-4 py-3.5 text-sm sm:text-base border border-gray-300/70 dark:border-gray-600/70 bg-white/90 dark:bg-gray-700/90 text-gray-900 dark:text-white rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 backdrop-blur-sm"
                placeholder="000.000.000-00"
              >
            </div>
            
            <div class="lg:col-span-3">
              <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Data de Nascimento <span class="text-red-500">*</span>
              </label>
              <input 
                v-model="form.dataNascimento" 
                type="date" 
                required
                class="w-full px-4 py-3.5 text-sm sm:text-base border border-gray-300/70 dark:border-gray-600/70 bg-white/90 dark:bg-gray-700/90 text-gray-900 dark:text-white rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 backdrop-blur-sm max-w-full"
              >
            </div>
          </div>
        </div>

        <div class="border-b border-gray-200/70 dark:border-gray-700/70 pb-6">
          <h2 class="text-lg sm:text-xl font-bold text-gray-800 dark:text-white mb-4 flex items-center">
            <span class="w-8 h-8 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3 shadow-lg">3</span>
            Qual foi o Evento Adverso? <span class="text-red-500">*</span>
          </h2>
          
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label v-for="(evento, index) in eventosAdversos" :key="index" class="flex items-start p-4 border border-gray-200/70 dark:border-gray-600/70 rounded-xl hover:bg-gray-50/80 dark:hover:bg-gray-700/50 cursor-pointer transition-all duration-200 backdrop-blur-sm group">
              <input 
                v-model="form.tipoEvento" 
                :value="evento" 
                type="radio"
                name="tipoEvento"
                class="w-5 h-5 mt-0.5 text-blue-600 border-gray-300 dark:border-gray-500 rounded focus:ring-blue-500 transition-colors"
              >
              <span class="ml-3 text-sm sm:text-base text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white transition-colors leading-relaxed">{{ evento }}</span>
            </label>
          </div>
          
          <Transition
            enter-active-class="transition-all duration-300 ease-out"
            enter-from-class="opacity-0 max-h-0"
            enter-to-class="opacity-100 max-h-32"
            leave-active-class="transition-all duration-200 ease-in"
            leave-from-class="opacity-100 max-h-32"
            leave-to-class="opacity-0 max-h-0"
          >
            <div v-if="form.tipoEvento === 'Outro'" class="mt-4 overflow-hidden">
              <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Especifique outro evento:
              </label>
              <input 
                v-model="form.outroEvento" 
                type="text"
                required
                class="w-full px-4 py-3.5 border border-gray-300/70 dark:border-gray-600/70 bg-white/90 dark:bg-gray-700/90 text-gray-900 dark:text-white rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 backdrop-blur-sm"
                placeholder="Descreva detalhadamente o evento"
              >
            </div>
          </Transition>
        </div>

        <div class="border-b border-gray-200/70 dark:border-gray-700/70 pb-6">
          <h2 class="text-lg sm:text-xl font-bold text-gray-800 dark:text-white mb-4 flex items-center">
            <span class="w-8 h-8 bg-gradient-to-r from-purple-500 to-violet-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3 shadow-lg">4</span>
            Local e Condições
          </h2>
          
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Local que Ocorreu o Evento <span class="text-red-500">*</span>
              </label>
              
              <select 
                v-model="form.tipoLocal" 
                @change="handleLocalChange"
                required
                class="w-full px-4 py-3.5 mb-3 border border-gray-300/70 dark:border-gray-600/70 bg-white/90 dark:bg-gray-700/90 text-gray-900 dark:text-white rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 backdrop-blur-sm"
              >
                <option value="">Selecione um local...</option>
                <option v-for="local in locais" :key="'local-' + local.id" :value="local.nome">
                  {{ local.nome }}
                </option>
                <option value="__OUTRO__">Outro</option>
              </select>

              <!-- Campo de texto para Outro -->
              <input 
                v-if="form.tipoLocal === '__OUTRO__'"
                v-model="form.localEvento" 
                type="text" 
                required
                placeholder="Digite o local..."
                class="w-full px-4 py-3.5 border border-gray-300/70 dark:border-gray-600/70 bg-white/90 dark:bg-gray-700/90 text-gray-900 dark:text-white rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 backdrop-blur-sm"
              >
            </div>
            
            <div>
              <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                Condições do Usuário <span class="text-red-500">*</span>
              </label>
              <div class="space-y-2">
                <label v-for="condicao in condicoesUsuario" :key="condicao" class="flex items-center p-3 border border-gray-200/70 dark:border-gray-600/70 rounded-xl hover:bg-gray-50/80 dark:hover:bg-gray-700/50 cursor-pointer transition-all duration-200 backdrop-blur-sm group">
                  <input 
                    v-model="form.condicoesUsuario" 
                    :value="condicao" 
                    type="radio"
                    name="condicoesUsuario"
                    required
                    class="w-5 h-5 text-purple-600 border-gray-300 dark:border-gray-500 focus:ring-purple-500 transition-colors"
                  >
                  <span class="ml-3 text-sm sm:text-base text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">{{ condicao }}</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <div class="border-b border-gray-200/70 dark:border-gray-700/70 space-y-6 pb-6">
          <h2 class="text-lg sm:text-xl font-bold text-gray-800 dark:text-white mb-4 flex items-center">
            <span class="w-8 h-8 bg-gradient-to-r from-red-500 to-rose-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3 shadow-lg">5</span>
            Descrições Detalhadas
          </h2>
          
          <div>
            <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              1 - Resumo do Evento Adverso <span class="text-red-500">*</span>
            </label>
            <textarea 
              v-model="form.resumoEvento" 
              required
              rows="4"
              class="w-full px-4 py-3.5 text-sm sm:text-base border border-gray-300/70 dark:border-gray-600/70 bg-white/90 dark:bg-gray-700/90 text-gray-900 dark:text-white rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all resize-none duration-200 backdrop-blur-sm"
              placeholder="Descreva detalhadamente o que aconteceu, incluindo circunstâncias, horário e pessoas envolvidas..."
            ></textarea>
          </div>
          
          <div>
            <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              2 - Ação de Correção Imediata <span class="text-red-500">*</span>
            </label>
            <textarea 
              v-model="form.acaoCorrecao" 
              required
              rows="4"
              class="w-full px-4 py-3.5 text-sm sm:text-base border border-gray-300/70 dark:border-gray-600/70 bg-white/90 dark:bg-gray-700/90 text-gray-900 dark:text-white rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all resize-none duration-200 backdrop-blur-sm"
              placeholder="Descreva as ações imediatas tomadas para corrigir o problema e prevenir reincidências..."
            ></textarea>
          </div>
        </div>

        <div class="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-6 border-t border-gray-200/70 dark:border-gray-700/70">
          <button 
            type="button" 
            @click="resetForm"
            class="flex-1 px-6 py-4 border-2 border-gray-300/70 dark:border-gray-600/70 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50/80 dark:hover:bg-gray-700/50 transition-all font-semibold text-sm sm:text-base backdrop-blur-sm transform hover:scale-[1.02] active:scale-[0.98]"
          >
            🗑️ Limpar Formulário
          </button>
          <button 
            type="button"
            @click="prepareSubmit"
            :disabled="isSubmitting"
            class="flex-1 px-6 py-4 bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed text-white rounded-xl transition-all font-bold text-sm sm:text-base shadow-xl hover:shadow-2xl transform hover:scale-[1.02] active:scale-[0.98] backdrop-blur-sm"
          >
            <span v-if="!isSubmitting" class="flex items-center justify-center">
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"></path>
              </svg>
              Registrar Evento Adverso
            </span>
            <span v-else class="flex items-center justify-center">
              <svg class="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Processando...
            </span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, onMounted } from 'vue'
import apiClient from '@/services/authService'

const eventosAdversos = [
  '1. Óbito',
  '2. Procedimento ou exame errado',
  '3. Procedimento ou exame em local errado',
  '4. Procedimento ou exame em usuário errado',
  '5. Falha na identificação do usuário',
  '6. Erro na prescrição médica',
  '7. Erro na administração de medicamentos',
  '8. Infiltração de soro ou medicamento',
  '9. Extravasamento de contraste',
  '10. Queda',
  '11. Reação medicamentosa',
  '12. Reação alérgica',
  '13. Diagnóstico ou laudo errado',
  '14. Atraso na entrega do laudo',
  'Outro'
]

const condicoesUsuario = [
  'Sozinho',
  'Com acompanhante',
  'Com profissional'
]

const form = reactive({
  responsavel: '',
  dataEvento: '',
  nomeUsuario: '',
  cpfUsuario: '',
  dataNascimento: '',
  tipoEvento: '',
  outroEvento: '',
  tipoLocal: '',
  localEvento: '',
  condicoesUsuario: '',
  resumoEvento: '',
  acaoCorrecao: '',
  consentimento_dado: false
})

const isSubmitting = ref(false)
const protocolo = ref('')
const showSuccess = ref(false)
const showError = ref(false)
const errorMessage = ref('')
const isDark = ref(false)
const locais = ref([])




// Theme functionality
const toggleTheme = () => {
  isDark.value = !isDark.value
  if (isDark.value) {
    document.documentElement.classList.add('dark')
    localStorage.setItem('theme', 'dark')
  } else {
    document.documentElement.classList.remove('dark')
    localStorage.setItem('theme', 'light')
  }
}

// Carregar locais
const loadLocais = async () => {
  try {
    const response = await apiClient.get('/api/v1/eventos-adversos/locais/')
    locais.value = response.data
  } catch (err) {
    console.error('Erro ao carregar locais:', err)
    // Se der erro, continua com array vazio (não bloqueia o formulário)
    locais.value = []
  }
}

// Handler para mudança de local
const handleLocalChange = () => {
  if (form.tipoLocal === '__OUTRO__') {
    form.localEvento = ''
  } else {
    form.localEvento = form.tipoLocal
  }
}


onMounted(() => {
  const savedTheme = localStorage.getItem('theme')
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
  
  if (savedTheme === 'dark' || (!savedTheme && prefersDark)) {
    isDark.value = true
    document.documentElement.classList.add('dark')
  } else {
    isDark.value = false
    document.documentElement.classList.remove('dark')
  }
  
  // Carregar locais ao montar o componente
  loadLocais()
})

// Notification handlers
const closeSuccess = () => {
  showSuccess.value = false
  protocolo.value = ''
}

const closeError = () => {
  showError.value = false
  errorMessage.value = ''
}

// Form submission
const submitForm = async () => {
  if (isSubmitting.value) return
  
  // Clear previous notifications
  showSuccess.value = false
  showError.value = false
  
  isSubmitting.value = true
  
  try {

    
    const payload = {
      responsavel: form.responsavel,
      data_ocorrencia: form.dataEvento,
      paciente: form.nomeUsuario,
      cpf_usuario: form.cpfUsuario,
      data_nascimento: form.dataNascimento,
      tipo_evento: form.tipoEvento === 'Outro' ? '' : form.tipoEvento,
      outro_evento: form.tipoEvento === 'Outro' ? (form.outroEvento || '') : '',
      local: form.tipoLocal === '__OUTRO__' ? form.localEvento : form.tipoLocal,
      descricao: form.resumoEvento,
      condicoes_usuario: form.condicoesUsuario,
      acao_correcao: form.acaoCorrecao,
      gravidade: 'A ser avaliada',
      medico_responsavel: ''
    }

    const response = await apiClient.post('/api/v1/eventos-adversos/', payload)

    if (response.status >= 200 && response.status < 300) {
      const protocolNumber = response.data?.protocolo || response.data?.protocol || `EA-${Date.now().toString().slice(-6)}`
      protocolo.value = protocolNumber
      
      // Reset form immediately
      resetForm()
      
      // Show success notification
      showSuccess.value = true
      
      // Scroll to top smoothly
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      })
      
      // Auto-hide after 6 seconds
      setTimeout(() => {
        showSuccess.value = false
        protocolo.value = ''
      }, 6000)
    } else {
      throw new Error(response.data?.message || 'Erro ao registrar evento')
    }
  } catch (error) {
    console.error('Erro completo:', error)
    
    let errorMsg = 'Erro de conexão. Tente novamente.'
    
    if (error.response) {
      errorMsg = error.response.data?.message || 
                 error.response.data?.detail || 
                 error.response.statusText || 
                 'Erro ao processar solicitação'
    } else if (error.request) {
      errorMsg = 'Sem resposta do servidor. Verifique sua conexão.'
    } else if (error.message) {
      errorMsg = error.message
    }
    
    errorMessage.value = errorMsg
    showError.value = true
    
    // Scroll to top smoothly
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    })
    
    // Auto-hide after 8 seconds
    setTimeout(() => {
      showError.value = false
      errorMessage.value = ''
    }, 8000)
  } finally {
    isSubmitting.value = false
  }
}



const prepareSubmit = async () => {
  const localValido = form.tipoLocal && (form.tipoLocal !== '__OUTRO__' || form.localEvento)
  const outroEventoValido = form.tipoEvento !== 'Outro' || !!form.outroEvento
  
  if (
    !form.dataEvento ||
    !form.nomeUsuario ||
    !form.dataNascimento ||
    !form.tipoEvento ||
    !outroEventoValido ||
    !localValido ||
    !form.condicoesUsuario ||
    !form.resumoEvento
  ) {
    errorMessage.value = 'Por favor, preencha todos os campos obrigatórios.'
    showError.value = true
    
    // Auto-hide error after 5 seconds
    setTimeout(() => {
      showError.value = false
      errorMessage.value = ''
    }, 5000)
    
    // Scroll to top to show error
    window.scrollTo({ top: 0, behavior: 'smooth' })
    return
  }

  submitForm()
}

// Form reset
const resetForm = () => {
  // Reset all form fields
  form.responsavel = ''
  form.dataEvento = ''
  form.nomeUsuario = ''
  form.cpfUsuario = ''
  form.dataNascimento = ''
  form.tipoEvento = ''
  form.outroEvento = ''
  form.tipoLocal = ''
  form.localEvento = ''
  form.condicoesUsuario = ''
  form.resumoEvento = ''
  form.acaoCorrecao = ''
  form.consentimento_dado = false

}

// CPF formatting function
const formatCPF = (event) => {
  let value = event.target.value.replace(/\D/g, '') // Remove all non-digits
  
  if (value.length <= 11) {
    // Format as XXX.XXX.XXX-XX
    if (value.length > 9) {
      value = value.replace(/(\d{3})(\d{3})(\d{3})(\d{0,2})/, '$1.$2.$3-$4')
    } else if (value.length > 6) {
      value = value.replace(/(\d{3})(\d{3})(\d{0,3})/, '$1.$2.$3')
    } else if (value.length > 3) {
      value = value.replace(/(\d{3})(\d{0,3})/, '$1.$2')
    }
  } else {
    // Limit to 11 digits maximum
    value = value.slice(0, 11)
    value = value.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
  }
  
  form.cpfUsuario = value
}
</script>
